import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { hashHistory, withRouter } from 'react-router';
import { LocalizedMessage } from 'intl/LocalizedComponents';
import withSizes from 'react-sizes';
import _concat from 'lodash/concat';
import _debounce from 'lodash/debounce';
import _delay from 'lodash/delay';
import _filter from 'lodash/filter';
import _find from 'lodash/find';
import _floor from 'lodash/floor';
import _includes from 'lodash/includes';
import _isEmpty from 'lodash/isEmpty';
import _isEqual from 'lodash/isEqual';
import _isNil from 'lodash/isNil';
import _map from 'lodash/map';
import _omit from 'lodash/omit';
import _random from 'lodash/random';
import _reject from 'lodash/reject';
import _size from 'lodash/size';
import _sum from 'lodash/sum';
import _times from 'lodash/times';
import _toString from 'lodash/toString';
import _uniqWith from 'lodash/uniqWith';
import _without from 'lodash/without';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import queryString from 'query-string';
// Core components
import Switch from 'components/core/Switch';
import Table from 'components/core/table/Table';
import TableHead from 'components/core/table/TableHead';
import TableToolbar from 'components/core/table/TableToolbar';
// Models
import mListParam from 'models/listParam';
// Custom
import { TABLE_ROW_HEIGHT } from 'constants/App';
import { updateListLastParams } from 'actions';
import {
  listFeatureShape, listShape, paginationParamShape, sortParamShape, filterParamShape,
} from 'constants/PropTypesShapes';
import { invoke, getWindowWidthRange } from 'utils/Tools';
import withPermissions from 'hocs/WithPermissions';
import withScrollerManagement from 'hocs/WithScrollerManagement';
import ErrorsDisplayer from 'components/misc/ErrorsDisplayer';
import ScrollerElement from 'components/misc/ScrollerElement';
import ListTemplateRow from './ListTemplateRow';
import ListTemplateRowLoading from './ListTemplateRowLoading';

class ListTemplate extends React.Component {
  constructor(props) {
    super(props);

    this.handleApplyPagination = this.handleApplyPagination.bind(this);
    this.handleApplySort = this.handleApplySort.bind(this);
    this.handleApplyFilters = this.handleApplyFilters.bind(this);
    this.handleShowHideDetailsElement = this.handleShowHideDetailsElement.bind(this);
    this.handleSelectAll = this.handleSelectAll.bind(this);
    this.handleClickElement = this.handleClickElement.bind(this);
    this.handleDoubleClickElement = this.handleDoubleClickElement.bind(this);
    this.handleAutoRefreshChange = this.handleAutoRefreshChange.bind(this);
    this.handleRowAskForDisplay = this.handleRowAskForDisplay.bind(this);

    this.doGetElementsList = this.doGetElementsList.bind(this);

    // Mount flag
    this._isMounted = false;

    // A reference for the whole table
    this.tableRef = React.createRef();

    // Create a reference for this list
    this.listRef = `list_${_random(10000, 99999)}`;

    // Desynchro reload management
    this.performRefreshAfterDesynchro = _debounce(this.doRefresh, 2000);

    // Row ask for display management
    this.performRowAskForDisplay = _debounce(this.doManageRowDisplay, 100);

    // First load flag
    this.firstLoad = true;

    // Refresh flag
    this.rowsQuantityBeforeRefresh = 0;

    const { elementsData: { handleGetList }, defaultAutoRefresh } = this.props;

    this.state = {
      loading: !!handleGetList,
      displayLoading: false,
      loadingRowsQuantity: 0,
      pagination: null,
      sort: null,
      filters: null,
      elementsWithDetails: [],
      autoRefresh: defaultAutoRefresh,
      displayingRows: false,
      applyDefaultFilters: true,
    };
  }

  componentDidMount() {
    this._isMounted = true;

    const { lastParams } = this.props;
    this.doGetElementsList(lastParams);
  }

  componentDidUpdate(prevProps) {
    const {
      needRefresh,
      elementsData: {
        loading,
        desynchronized,
        elements,
      },
      errors,
      windowHeight,
    } = this.props;
    const { needRefresh: prevNeedRefresh, elementsData: prevElementsData, errors: prevErrors } = prevProps;
    const { autoRefresh } = this.state;

    // Check if a refresh is needed
    if (!prevNeedRefresh && needRefresh) {
      this.rowsQuantityBeforeRefresh = _size(elements.data);
      this.doRefresh();
    }
    // Remove loading once elements are loaded
    if (prevElementsData.loading && !loading) {
      this.rowsQuantityBeforeRefresh = 0;
      invoke(() => {
        this.setState({
          loading: false,
        });
      });
    }
    // Set loading flag
    if (!prevElementsData.loading && loading) {
      // Get the top position of the table
      const tableTop = this.tableRef.current.getBoundingClientRect().top;
      // Calculate the quantity of loading rows that must be displayed
      let loadingRowsQuantity = _floor((windowHeight - tableTop) / TABLE_ROW_HEIGHT) - 2;
      // In case of refresh, take the quantity of rows displayed, if it is not greater than calculated value
      if (this.rowsQuantityBeforeRefresh > 0 && this.rowsQuantityBeforeRefresh < loadingRowsQuantity) {
        loadingRowsQuantity = this.rowsQuantityBeforeRefresh;
      }

      invoke(() => {
        this.setState({
          loading: true,
          loadingRowsQuantity,
        }, () => {
          _delay(() => {
            if (this._isMounted) {
              // Display loading after a delay to avoid displaying it on short loadings
              this.setState({ displayLoading: true });
            }
          }, 1000);
        });
      });
    }

    // Desynchro catching
    if (desynchronized && autoRefresh) {
      // Debounce a refresh
      this.performRefreshAfterDesynchro();
    } else {
      // Cancel current debounce, if any
      this.performRefreshAfterDesynchro.cancel();
    }

    // Errors caching
    if (errors && !prevErrors) {
      // Scroll to error displayer
      const { handleScrollTo } = this.props;
      handleScrollTo(this.listRef, 'errorDisplayer');
    }
  }

  componentWillUnmount() {
    this._isMounted = false;

    // Cancel current debounce, if any
    this.performRefreshAfterDesynchro.cancel();

    // Reinit rows observation elements
    this.rowsToRender = {};
  }

  // Handlers
  handleApplyPagination(pagination) {
    this.doGetElementsList({ pagination, paginationUserAction: true });
  }

  handleApplySort(sort) {
    const { pagination: { pageSize } } = this.state;
    // When appying new sort, reload first page
    this.doGetElementsList({
      sort,
      sortUserAction: true,
      pagination: { page: 1, pageSize },
      paginationUserAction: true,
    });
  }

  handleApplyFilters(filters) {
    const { pagination: { pageSize } } = this.state;
    // When appying filters, reload first page
    this.doGetElementsList({
      filters,
      filtersUserAction: true,
      pagination: { page: 1, pageSize },
      paginationUserAction: true,
    });
  }

  handleShowHideDetailsElement(elementId) {
    this.setState((state) => ({
      elementsWithDetails: _includes(state.elementsWithDetails, elementId) ? _without(state.elementsWithDetails, elementId) : [...state.elementsWithDetails, elementId],
    }));
  }

  handleSelectAll(checked) {
    const { elementsData, onSelectElements } = this.props;
    const displayedElements = elementsData.elements.data;
    const { selectedElements } = elementsData;

    // Get the already selected elements non displayed
    const nonDisplayedSelectedElements = _reject(selectedElements, (el) => _find(displayedElements, ['id', el.id]));

    if (checked) {
      // All visible rows must be selected
      // Merge non displayed selected elements with all displayed elements
      onSelectElements([
        ...displayedElements,
        ...nonDisplayedSelectedElements,
      ]);
    } else {
      // All visible rows must be unselected
      // Return only non displayed selected elements
      onSelectElements([
        ...nonDisplayedSelectedElements,
      ]);
    }
  }

  handleClickElement(element) {
    const {
      elementsData, multiSelectable, onRowClick, onSelectElements,
    } = this.props;

    if (onRowClick) {
      // If parent defined a 'onRowClick' function, use it
      onRowClick(element);
    } else {
      // Otherwise, manage row selection
      const { selectedElement, selectedElements } = elementsData;

      if (onSelectElements) {
        if (multiSelectable) {
          if (_find(selectedElements, ['id', element.id])) {
            // If the element is already in the selected ones: remove it
            onSelectElements(_reject(selectedElements, ['id', element.id]));
          } else {
            // Add element to selected ones
            onSelectElements([...selectedElements, element]);
          }
        } else if (selectedElement && element.id === selectedElement.id) {
          // The clicked element is the selected one => deselect it
          onSelectElements(null);
        } else {
          onSelectElements(element);
        }
      }
    }
  }

  handleDoubleClickElement(element) {
    const { onRowDbleClick } = this.props;
    if (onRowDbleClick) {
      onRowDbleClick(element);
    }
  }

  handleAutoRefreshChange(checked) {
    this.setState({
      autoRefresh: checked,
    });
  }

  handleRowAskForDisplay() {
    this.setState({
      displayingRows: true,
    }, this.performRowAskForDisplay);
  }

  // Private
  doManageRowDisplay() {
    this.setState({
      displayingRows: false,
    });
  }

  doRefresh() {
    this.doGetElementsList();
  }

  doGetElementsList(params) {
    const {
      name,
      location: { pathname, query },
      listFeatures: { defaultParams, columns, searchFields },
      withQueryParams,
      permanentFilters,
      pageSize,
      handleUpdateListLastParams,
      defaultFilters,
    } = this.props;
    const {
      pagination,
      paginationUserAction,
      sort,
      sortUserAction,
      filters,
      filtersUserAction,
    } = params || {};
    const { applyDefaultFilters } = this.state;
    let defaultFiltersApplied = false;

    // For pagination/sort/filters, use:
    // - params if no corresponding element can be found in query
    // - params if it comes from user action
    // - query in other cases
    let paramsPagination = (!query.pagination || paginationUserAction) ? pagination : null;
    let paramsSort = (!query.sort || sortUserAction) ? sort : null;
    let paramsFilters = (!query.filters || filtersUserAction) ? filters : null;

    if (applyDefaultFilters && !paramsFilters && !query.filters) {
      paramsFilters = defaultFilters;
      defaultFiltersApplied = true;
    }

    if (filtersUserAction) {
      this.setState({ applyDefaultFilters: false });
    }

    // If a pageSize is defined (i.e. retrieved from local storage), use it as default if nothing else is defined
    const storedPagination = (!_isNil(pageSize) && withQueryParams) ? { page: 1, pageSize } : null;
    const serializedStoredPagination = storedPagination ? mListParam.pagination.serialize(storedPagination) : null;

    // Update query params, if necessary
    if (withQueryParams) {
      // Update last params
      handleUpdateListLastParams(name, false,
        mListParam.pagination.deserialize(query, paramsPagination, storedPagination),
        mListParam.sort.deserialize(query, paramsSort, null, columns),
        mListParam.filters.deserialize(query, paramsFilters, searchFields));

      const newPagination = paramsPagination ? mListParam.pagination.serialize(pagination) : (query.pagination || serializedStoredPagination);
      const newSort = paramsSort ? mListParam.sort.serialize(sort) : query.sort;
      const newFilters = paramsFilters ? mListParam.filters.serialize(filters || (defaultFiltersApplied ? defaultFilters : null)) : query.filters;

      // Build new query params
      let newQueryParams = _omit(query, ['pagination', 'sort', 'filters']);
      if (newPagination) {
        newQueryParams = {
          ...newQueryParams,
          pagination: newPagination,
        };
      }
      if (newSort) {
        newQueryParams = {
          ...newQueryParams,
          sort: newSort,
        };
      }
      if (newFilters) {
        newQueryParams = {
          ...newQueryParams,
          filters: newFilters,
        };
      }

      // Update query params in URL
      hashHistory.replace({
        pathname,
        search: `?${queryString.stringify(newQueryParams, { encode: true })}`,
      });
    } else {
      // No query params managed: use state to retrieve current params
      const {
        pagination: statePagination,
        sort: stateSort,
        filters: stateFilters,
      } = this.state;
      if (!paramsPagination) {
        paramsPagination = statePagination;
      }
      if (!paramsSort) {
        paramsSort = stateSort;
      }
      if (!paramsFilters) {
        paramsFilters = stateFilters;
      }
    }

    // Build filters with optional permanent filters
    const baseFilters = mListParam.filters.deserialize(withQueryParams && query, paramsFilters, searchFields) || [];
    const finalFilters = permanentFilters ? _uniqWith(_concat(baseFilters, permanentFilters), _isEqual) : baseFilters;

    // Update local state
    this.setState({
      displayLoading: false,
      pagination: mListParam.pagination.deserialize(withQueryParams && query, paramsPagination, storedPagination || defaultParams.pagination),
      sort: mListParam.sort.deserialize(withQueryParams && query, paramsSort, defaultParams.sort, columns),
      filters: finalFilters,
    }, this.doLaunchGetList);
  }

  doLaunchGetList() {
    const { elementsData } = this.props;
    const { pagination, sort, filters } = this.state;
    // Get elements list
    if (elementsData.handleGetList) {
      elementsData.handleGetList(pagination, sort, filters, this.firstLoad);
    }

    // Update first load
    this.firstLoad = false;
  }

  render() {
    const {
      loading,
      displayLoading,
      loadingRowsQuantity,
      sort,
      filters,
      elementsWithDetails,
      autoRefresh,
      displayingRows,
    } = this.state;
    const {
      hasPermission, stickyHeader, withBorder, windowWidthRange, name, listFeatures,
      withSelection, multiSelectable, withChoose, chooseTooltipId,
      withActions, nbActions, withDetails, withPagination, withSort, withFooter,
      elementsData: { elements, selectedElement, selectedElements }, withAutoRefresh, withVirtualization,
      excludedFilterFields, errors, filtersComp, rowComp, rowCompDetails,
    } = this.props;

    // Get columns
    const { columns } = listFeatures;

    // Calculate the number of displayed columns
    let displayedColumnsQuantity = 0;
    if (listFeatures.columnsQuantity) {
      const columnsQuantity = listFeatures.columnsQuantity(hasPermission);
      displayedColumnsQuantity = _sum([columnsQuantity[windowWidthRange], withSelection | 0, withChoose | 0, withActions | 0, withDetails | 0]);
    }

    // Array of elements
    const elementsArray = elements.data;
    const hasElements = !_isEmpty(elementsArray);

    // Select all state
    let selectAllState = false;
    if (multiSelectable) {
      // Get the displayed selected elements
      const displayedSelectedElements = _filter(selectedElements, (el) => _find(elementsArray, ['id', el.id]));
      if (_size(displayedSelectedElements) > 0) {
        selectAllState = _size(displayedSelectedElements) === _size(elementsArray) || 'intermediate';
      }
    }

    // Wait elements ready before rendering...
    if (!!sort && !!filters) {
      return (
        <React.Fragment>
          <ScrollerElement target="errorDisplayer" name={this.listRef}>
            <ErrorsDisplayer errors={errors} noFormRef />
          </ScrollerElement>
          {filtersComp && (
            <filtersComp.type
              {...filtersComp.props}
              disabled={loading && displayLoading}
              filtersParam={filters}
              excludedFields={excludedFilterFields || []}
              onApplyFilters={this.handleApplyFilters}
            />
          )}
          <div className={classnames({ 'box box-default m-0': withBorder })} ref={this.tableRef}>
            {multiSelectable && (
              <TableToolbar
                highlight
                title={(
                  <LocalizedMessage id="info.nbSelectedElements" values={{ nbSelected: _size(selectedElements) }} />
                )}
              />
            )}
            {withAutoRefresh && (
              <TableToolbar
                actions={[
                  <Switch
                    key="autoRefresh"
                    label={<LocalizedMessage id="field.autoRefresh" />}
                    checked={autoRefresh}
                    onChange={this.handleAutoRefreshChange}
                  />,
                ]}
              />
            )}
            <Table
              stickyHeader={!!stickyHeader && windowWidthRange !== 'xs'}
              loading={loading}
              displayLoading={displayLoading}
              hasElements={hasElements}
              head={(
                <TableHead
                  columns={columns}
                  withSort={withSort}
                  sortParam={sort}
                  manageSelectAll={multiSelectable}
                  selectAllState={selectAllState}
                  adjustForCheckbox={withSelection}
                  adjustForActions={withActions}
                  adjustForChoose={withChoose}
                  adjustForDetails={withDetails}
                  nbActions={nbActions}
                  stickyHeaderStyle={windowWidthRange !== 'xs' ? stickyHeader : null}
                  onSortChange={this.handleApplySort}
                  onSelectAllCheck={this.handleSelectAll}
                />
              )}
              withFooter={withFooter}
              pagination={{
                withActions: withPagination,
                currentPage: withPagination ? elements.pageNumber : 0,
                pageSize: withPagination ? elements.pageSize : 0,
                totalPages: withPagination ? elements.totalPages : 0,
                totalEntries: elements.totalEntries,
                onChange: this.handleApplyPagination,
              }}
            >
              {_times(loadingRowsQuantity, (index) => (
                <ListTemplateRowLoading
                  key={`loading_${index}`}
                  loading={loading}
                  columnsQuantity={displayedColumnsQuantity}
                />
              ))}
              {!loading && hasElements && (
                _map(elementsArray, (element) => (
                  <ListTemplateRow
                    key={_toString(element.id)}
                    rowComp={rowComp}
                    rowCompDetails={rowCompDetails}
                    name={name}
                    element={element}
                    columnsQuantity={displayedColumnsQuantity}
                    withSelection={withSelection || false}
                    selected={
                      (multiSelectable && _includes(_map(selectedElements, 'id'), element.id))
                      || (selectedElement && selectedElement.id === element.id)
                      || false
                    }
                    withChoose={withChoose || false}
                    chooseTooltipId={chooseTooltipId}
                    withActions={withActions || false}
                    nbActions={nbActions || 0}
                    withDetails={withDetails || false}
                    showDetails={_includes(elementsWithDetails, element.id)}
                    withVirtualization={withVirtualization}
                    onShowHideDetails={this.handleShowHideDetailsElement}
                    onClick={this.handleClickElement}
                    onDoubleClick={this.handleDoubleClickElement}
                    waitForDisplay={displayingRows}
                    onAskForDisplay={this.handleRowAskForDisplay}
                  />
                ))
              )}
            </Table>
          </div>
        </React.Fragment>
      );
    }
    return null;
  }
}

ListTemplate.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    query: PropTypes.shape({
      sort: PropTypes.string,
      filters: PropTypes.string,
      pagination: PropTypes.string,
    }),
  }).isRequired,
  hasPermission: PropTypes.func.isRequired,
  stickyHeader: PropTypes.shape({
    top: PropTypes.number.isRequired,
  }),
  withBorder: PropTypes.bool,
  windowWidthRange: PropTypes.string.isRequired,
  windowHeight: PropTypes.number.isRequired,
  withQueryParams: PropTypes.bool,
  name: PropTypes.string.isRequired,
  listFeatures: listFeatureShape.isRequired,
  withSelection: PropTypes.bool,
  multiSelectable: PropTypes.bool,
  withChoose: PropTypes.bool,
  chooseTooltipId: PropTypes.string,
  withActions: PropTypes.bool,
  nbActions: PropTypes.number,
  withDetails: PropTypes.bool,
  withPagination: PropTypes.bool,
  withSort: PropTypes.bool,
  withFooter: PropTypes.bool,
  elementsData: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    desynchronized: PropTypes.bool,
    elements: listShape.isRequired,
    selectedElement: PropTypes.shape({
      id: PropTypes.number.isRequired,
    }),
    selectedElements: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
      }),
    ),
    handleGetList: PropTypes.func,
  }).isRequired,
  withAutoRefresh: PropTypes.bool,
  defaultAutoRefresh: PropTypes.bool,
  withVirtualization: PropTypes.bool,
  permanentFilters: PropTypes.arrayOf(filterParamShape),
  excludedFilterFields: PropTypes.arrayOf(PropTypes.string),
  errors: PropTypes.shape({
    global: PropTypes.string.isRequired,
  }),
  filtersComp: PropTypes.element,
  rowComp: PropTypes.elementType.isRequired,
  rowCompDetails: PropTypes.elementType,
  onSelectElements: PropTypes.func,
  onRowClick: PropTypes.func,
  onRowDbleClick: PropTypes.func,
  needRefresh: PropTypes.bool.isRequired,
  lastParams: PropTypes.shape({
    pagination: paginationParamShape,
    sort: PropTypes.arrayOf(sortParamShape),
    filters: PropTypes.arrayOf(filterParamShape),
  }),
  defaultFilters: PropTypes.arrayOf(filterParamShape),
  pageSize: PropTypes.number,
  handleUpdateListLastParams: PropTypes.func.isRequired,
  handleScrollTo: PropTypes.func.isRequired,
};

ListTemplate.defaultProps = {
  stickyHeader: null,
  withBorder: true,
  withQueryParams: true,
  withSelection: false,
  multiSelectable: false,
  withChoose: false,
  chooseTooltipId: null,
  withActions: false,
  nbActions: 1,
  withDetails: false,
  withPagination: true,
  withSort: true,
  withFooter: true,
  withAutoRefresh: false,
  defaultAutoRefresh: true,
  withVirtualization: true,
  rowCompDetails: null,
  onSelectElements: null,
  onRowClick: null,
  onRowDbleClick: null,
  permanentFilters: null,
  excludedFilterFields: null,
  errors: null,
  filtersComp: null,
  lastParams: null,
  pageSize: null,
  defaultFilters: null,
};

const mapStateToProps = (state, ownProps) => ({
  needRefresh: state.list.needRefresh,
  lastParams: state.list.lastParams[ownProps.name],
  pageSize: state.pageSize[ownProps.name],
});

const mapDispatchToProps = (dispatch) => ({
  handleUpdateListLastParams: (target, reinit, pagination, sort, filters) => {
    dispatch(updateListLastParams(target, reinit, pagination, sort, filters));
  },
});

const mapSizesToProps = ({ width, height }) => ({
  windowWidthRange: getWindowWidthRange(width),
  windowHeight: height,
});

module.exports = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withRouter,
  withPermissions,
  withSizes(mapSizesToProps),
  withScrollerManagement,
)(ListTemplate);



// WEBPACK FOOTER //
// ./components/templates/ListTemplate.js